/**
 * widgets/app-shell/ui/MobileNavDrawer — mobile navigation drawer.
 *
 * shared/ui/sidebar.tsx is a bare structural primitive with no built-in
 * mobile Sheet/overlay behaviour, so this drawer is built directly on the
 * Radix Dialog primitives already used by shared/ui/dialog.tsx (no new
 * dependency): an overlay + a left-anchored panel containing <AppNav/>.
 *
 * Closes on: overlay click, Escape (both handled natively by Radix Dialog),
 * and on clicking any nav item inside the panel.
 *
 * FSD: widgets may import from features, entities, and shared only.
 */

import * as DialogPrimitive from '@radix-ui/react-dialog';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { AppNav } from '@/widgets/app-nav';
import { Dialog, DialogOverlay, DialogPortal } from '@/shared/ui/dialog';
import { cn } from '@/shared/lib/cn';

export interface MobileNavDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MobileNavDrawer({ open, onOpenChange }: MobileNavDrawerProps) {
  const { t } = useTranslation('common');

  // Close the drawer when a nav item (an <a>) inside it is activated —
  // the click already navigated via AppNav's own SPA handler; this just
  // dismisses the overlay so the destination page is visible.
  function handleContentClick(e: MouseEvent<HTMLDivElement>) {
    const target = e.target as HTMLElement;
    if (target.closest('a')) {
      onOpenChange(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          data-testid="mobile-nav-drawer"
          onClick={handleContentClick}
          aria-describedby={undefined}
          className={cn(
            'fixed inset-y-0 left-0 z-50 flex h-full w-64 flex-col gap-4 border-r bg-background p-4 shadow-lg outline-none',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left',
            'duration-200',
          )}
        >
          <DialogPrimitive.Title className="sr-only">
            {t('shell.nav.mainLabel')}
          </DialogPrimitive.Title>
          <div className="flex items-center justify-between">
            <span className="truncate text-base font-bold" aria-hidden="true">
              {t('app.name')}
            </span>
            <DialogPrimitive.Close
              className="flex h-11 w-11 items-center justify-center rounded-md hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              aria-label={t('shell.nav.closeMenu')}
              data-testid="mobile-nav-close"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </DialogPrimitive.Close>
          </div>
          <div className="flex-1 overflow-y-auto">
            <AppNav />
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
